import _ from 'lodash'
import { defineComponent } from 'vue'
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons'
import { switchMap } from 'rxjs/operators'

import { Container, Item } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { ContainerDataStore, ItemDataStore } from '@skinsoft/entity'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup'
import { ChooseItemModal } from '../../../../entity/component/choose-item-modal'
import { SelectItemMultipleModal } from '../../../../entity/component/select-item-multiple-modal'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'

const PHYSICAL_CONTAINER_TYPE = SEARCH_TYPES.physicalContainerItem
const NUMERIC_CONTAINER_TYPE = SEARCH_TYPES.numericContainerItem
const CONTAINERS_TYPE = [PHYSICAL_CONTAINER_TYPE, NUMERIC_CONTAINER_TYPE]

export const MapIconToggle = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sLocationSelectorPopup: LocationSelectorPopup,
        sChooseItemModal: ChooseItemModal,
    },
    template: require('./map-icon-toggle.html'),
    data() {
        return {
            mapIcon: faLocationArrow,
            selectedItem: null as Item | null,
            commonResearchTypes: [
                'physicalContainerItem',
                'numericContainerItem',
                'physicalAccessoryItem',
                'numericAccessoryItem',
            ],
            specificResearchTypes: {
                movies: [
                    'physicalFilmItem',
                    'NumericFilmItem'
                ],
                museum: [
                    'physicalMuseumItem',
                    'numericMuseumItem'
                ]
            },
            researchTypes: [] as string[],
        }
    },
    created() {
        this.$itemDataStore = ItemDataStore.getInstance()
    },
    methods: {
        openItemModal() {
            // workspaceType read from localStorage (set at login) instead of WorkspaceDataStore,
            // which can still be empty at this point depending on how the page was reached
            const workspaceType = localStorage.getItem('workspaceType') ?? ''
            this.researchTypes = this.commonResearchTypes.concat(this.specificResearchTypes[workspaceType] ?? [])
            ;(this.$refs.selectItemMapModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },

        onItemSelected({ id: itemId }: { id: string }) {
            (this.$itemDataStore as ItemDataStore).fetchOne(itemId).subscribe((item) => {
                this.selectedItem = item
                ;(this.$refs.chooseItemModal as InstanceType<typeof LocationSelectorPopup>).open()
                ;(this.$refs.selectItemMapModal as InstanceType<typeof SelectItemMultipleModal>).close()
            })
        },

        onLocationValidated(location: {
            location: { id: string }
            setPermanentLocation: boolean
        }) {
            if (this.selectedItem) {
                _.set(
                    this.selectedItem,
                    'locationItemSchema.realTimeLocation.realTimeLocation',
                    location.location.id,
                )
                _.set(
                    this.selectedItem,
                    'locationItemSchema.realTimeLocation.effectiveDate.skinIntervalComplexes[0].startInterval.date',
                    Date.now(),
                )
                _.set(
                    this.selectedItem,
                    'locationItemSchema.realTimeLocation.effectiveDate.useDate',
                    'DATE'
                )
                if (location.setPermanentLocation) {
                    _.set(
                        this.selectedItem,
                        'locationItemSchema.permanentLocation.permanentLocation',
                        location.location.id,
                    )
                    _.set(
                        this.selectedItem,
                        'locationItemSchema.permanentLocation.effectiveDate.skinIntervalComplexes[0].startInterval.date',
                        Date.now(),
                    )
                    _.set(
                        this.selectedItem,
                        'locationItemSchema.permanentLocation.effectiveDate.useDate',
                        'DATE'
                    )
                }

                let request$ = (this.$itemDataStore as ItemDataStore).update(this.selectedItem.id, this.selectedItem)
                if(this.selectedItem.type && (CONTAINERS_TYPE as string[]).includes(this.selectedItem.type)) {
                    request$ = request$.pipe(
                        switchMap(updatedContainer => {
                            const containerType = this.selectedItem!.type
                            return (this.$containerDataStore as ContainerDataStore).cascadeLocationOnContent(updatedContainer.id)
                        })
                    )
                }

                request$.subscribe(
                    () =>
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('entity.mapModal.saveNotification.locationChanged'),
                            variant: 'success',
                        }),
                    () =>
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('entity.mapModal.saveNotification.errorMessage'),
                            variant: 'success',
                        }),
                )
            }
        },

        onContainerValidated({ container } : { container: Container }) {
            if (this.selectedItem) {
                if (
                    _.defaultTo(this.selectedItem?.conditioningItemSchema?.actualContainer, [])
                    .find(actualContainer => actualContainer.container === container.id)
                ) {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('entity.mapModal.saveNotification.itemAlreadyInContainer'),
                        variant: 'danger'
                    })
                } else {
                    _.set(
                        this.selectedItem,
                        'conditioningItemSchema.actualContainer',
                        _.defaultTo(this.selectedItem?.conditioningItemSchema?.actualContainer, []).concat([
                            {
                                container: container.id,
                                date: getCurrentDateAsSkinDate(),
                            },
                        ]),
                    )
                    ;(this.$itemDataStore as ItemDataStore).update(this.selectedItem.id, this.selectedItem).subscribe(
                        () =>
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                message: this.$t('entity.mapModal.saveNotification.containerChanged'),
                                variant: 'success',
                            }),
                        () =>
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                                message: this.$t('entity.mapModal.saveNotification.errorMessage'),
                                variant: 'danger',
                            }),
                    )
                }
            }
        },
    },
})
